import { ArrowLeft } from 'lucide-react';
import { useInfraSites } from '@/lib/api/queries';
import { useInfraStore } from '@/lib/store/infra';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dot } from '@/components/ui/dot';
import { Section, SectionGroup } from '@/components/Section';
import { InfoRow } from '@/components/InfoRow';
import { Accordion } from '@/components/ui/accordion';
import { formatAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { InfraSite } from '@/lib/api/types';

export function InfraDetail({ id }: { id: string }) {
  const { data, isPending } = useInfraSites();
  const hb = useInfraStore((s) => s.byId[id]);
  const select = useInfraStore((s) => s.select);
  const site = data?.find((s) => s.site_id === id);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <button onClick={() => select(null)} className="flex items-center gap-1 text-(--color-green) text-[0.846em] font-semibold mb-1 hover:opacity-80">
          <ArrowLeft size={13} /> Infrastructure
        </button>
        {site && (
          <>
            <h2 className="text-base font-bold">{site.site_id}</h2>
            <div className="flex items-center gap-1.5 mt-1">
              <Badge variant="muted">{site.type === 'F' ? 'Fixed' : 'Mobile'}</Badge>
              <Badge variant={site.status === 'active' ? 'ok' : site.status === 'degraded' ? 'warn' : 'crit'}>{site.status}</Badge>
              <span className="text-[0.615em] font-mono text-(--color-text3)">
                Updated {hb?.last_heartbeat ? formatAgo(hb.last_heartbeat) : '—'}
              </span>
            </div>
            {/* Mockup `#infra-detail-desc` (L3378) — description shown below
                the badge row, truncated if long. Hidden when absent. */}
            {site.description && (
              <div className="text-[0.692em] text-(--color-text3) mt-1 line-clamp-2">
                {site.description}
              </div>
            )}
          </>
        )}
      </header>
      <div className="flex-1 overflow-y-auto p-3">
        {isPending && <Skeleton className="h-32 w-full" />}
        {site && <InfraSiteBody site={site} latencyMs={hb?.latency_ms ?? site.status === 'inactive' ? null : 42} />}
      </div>
    </div>
  );
}

function InfraSiteBody({ site, latencyMs }: { site: InfraSite; latencyMs: number | null }) {
  const byCategory = site.components.reduce<Record<string, typeof site.components>>((acc, c) => {
    (acc[c.category] ??= []).push(c);
    return acc;
  }, {});
  return (
    <SectionGroup>
      <Section title="Location & Network">
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
          <InfoRow label="Address" value={site.address} />
          <InfoRow label="Coordinates" value={`${site.lat.toFixed(4)}, ${site.lng.toFixed(4)}`} mono />
          <InfoRow label="VPN IP" value={site.vpn_ip ?? '—'} mono />
          <InfoRow label="Serial" value={site.serial_number ?? '—'} mono />
          <InfoRow label="Latency" value={latencyMs != null ? `${latencyMs} ms` : '—'} mono />
        </div>
      </Section>
      <Section title="Backhaul">
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2 space-y-1.5">
          <BackhaulRow tier="Primary" value={site.backhaul_primary} />
          {site.backhaul_secondary && <BackhaulRow tier="Secondary" value={site.backhaul_secondary} />}
          {site.backhaul_tertiary && <BackhaulRow tier="Tertiary" value={site.backhaul_tertiary} />}
        </div>
      </Section>
      <Section title="Components">
        {Object.entries(byCategory).map(([cat, comps]) => (
          <Accordion key={cat} title={cat} meta={`${comps.filter((c) => c.online).length}/${comps.length} online`}>
            <ul className="space-y-1">
              {comps.map((c) => (
                <li key={c.component_id} className="rounded-md border border-(--color-border-lite) bg-(--color-surface) px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Dot variant={c.online ? 'ok' : 'crit'} size="xs" />
                      <span className="text-[0.769em] font-bold truncate">{c.name}</span>
                    </div>
                    <Badge variant={c.online ? 'ok' : 'crit'}>{c.online ? 'Online' : 'Offline'}</Badge>
                  </div>
                  <div className="text-[0.615em] font-mono text-(--color-text3) mt-0.5">
                    {c.make} {c.model} · {c.serial}{c.ip ? ` · ${c.ip}` : ''}
                  </div>
                  {/* Mockup component-uptime bar (L11479-L11560) — single-row
                      bar showing uptime_pct in green-amber-red ladder. */}
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex-1 h-1 rounded-full bg-(--color-border-lite) overflow-hidden">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          c.uptime_pct >= 99 ? 'bg-(--color-green)'
                          : c.uptime_pct >= 95 ? 'bg-(--color-amber)'
                          : 'bg-(--color-red)',
                        )}
                        style={{ width: `${Math.max(0, Math.min(100, c.uptime_pct))}%` }}
                      />
                    </div>
                    <span className="text-[0.538em] font-mono text-(--color-text3) shrink-0">
                      {c.uptime_pct.toFixed(1)}%
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </Accordion>
        ))}
      </Section>
    </SectionGroup>
  );
}

function BackhaulRow({ tier, value }: { tier: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-[0.769em]">
      <span className="text-(--color-text3) font-mono">{tier}</span>
      <Badge variant="info">{value}</Badge>
    </div>
  );
}
